/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { ActionButtonType, Switch } from "@octopusdeploy/design-system-components";
import type { EnvironmentResource, FeatureToggleEnvironmentResource, FeatureToggleResource, ProjectResource, TenantResource } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import { cloneDeep } from "lodash";
import * as React from "react";
import { useState } from "react";
import EditToggleDialog from "~/areas/projects/components/FeatureToggles/EditFeatureToggleEnvironmentDialog";
import ToggleScope from "~/areas/projects/components/FeatureToggles/FeatureToggleEnvironment";
import { useProjectContext } from "~/areas/projects/context/index";
import { repository } from "~/clientInstance";
import type { DoBusyTask, Errors } from "~/components/DataBaseComponent";
import { DataBaseComponent, useDoBusyTaskEffect } from "~/components/DataBaseComponent/index";
import DialogOpener from "~/components/Dialog/DialogOpener";
import OpenDialogButton from "~/components/Dialog/OpenDialogButton";
import SaveDialogLayout from "~/components/DialogLayout/SaveDialogLayout";
import { LegacyForm } from "~/components/FormPaperLayout/LegacyForm";
import Markdown from "~/components/Markdown/Markdown";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import type { MenuItem } from "~/components/OverflowMenu/OverflowMenu";
import { OverflowMenuItems } from "~/components/OverflowMenu/OverflowMenu";
import type { ConvertedOverflowMenuItems } from "~/components/OverflowMenu/OverflowMenuConverterVNext";
import { OverflowMenuConverterVNext } from "~/components/OverflowMenu/OverflowMenuConverterVNext";
import { PageContent } from "~/components/PageContent/PageContent";
import { RemoveItemsList } from "~/components/RemoveItemsList/RemoveItemsList";
import { ExpandableFormSection, FormSection, MarkdownEditor, required, Summary } from "~/components/form/index";
import NameSummaryWithSlug from "~/primitiveComponents/form/Slugs/NameSummaryWithSlug";
import SlugEditor from "~/primitiveComponents/form/Slugs/SlugEditor";
import Text from "~/primitiveComponents/form/Text/Text";
interface FeatureTogglePageProps {
    spaceId: string;
    featureToggleSlug: string;
}
interface FeatureTogglePageInternalProps extends FeatureTogglePageProps {
    doBusyTask: DoBusyTask;
    busy: Promise<void> | undefined;
    errors: Errors | undefined;
}
interface EditState {
    environmentEdit: FeatureToggleEnvironmentResource;
    environmentEditIdx: number;
}
class ToggleEnvironmentList extends RemoveItemsList<FeatureToggleEnvironmentResource> {
}
export class FeatureTogglePage extends DataBaseComponent<FeatureTogglePageProps> {
    constructor(props: FeatureTogglePageProps) {
        super(props);
        this.state = {};
    }
    render() {
        return <FeatureTogglePageInternal spaceId={this.props.spaceId} featureToggleSlug={this.props.featureToggleSlug} doBusyTask={this.doBusyTask} busy={this.state.busy} errors={this.errors}/>;
    }
    static displayName = "FeatureTogglePage";
}
function FeatureTogglePageInternal({ spaceId, featureToggleSlug, doBusyTask, busy, errors }: FeatureTogglePageInternalProps) {
    const projectContext = useProjectContext();
    const project = projectContext.state.model;
    const [lookupData, setLookupData] = useState<{
        environments: EnvironmentResource[];
        tenants: TenantResource[];
    } | null>(null);
    const [model, setModel] = useState<FeatureToggleResource | undefined>(undefined);
    const [cleanModel, setCleanModel] = useState<FeatureToggleResource | undefined>(undefined);
    const [overflowMenu, setOverflowMenu] = useState<ConvertedOverflowMenuItems>({ menuItems: [], dialogs: [] });
    const { navigate } = useSpaceAwareNavigation();
    useDoBusyTaskEffect(doBusyTask, async () => {
        const environmentsPromise = repository.Projects.getAvailableEnvironmentsForProject(project.Id);
        const tenantsPromise = repository.Tenants.all({ projectId: project.Id });
        const modelPromise = repository.FeatureToggles.get(project, featureToggleSlug);
        const environmentsResponse = await environmentsPromise;
        const model = await modelPromise;
        setLookupData({
            environments: environmentsResponse.Environments,
            tenants: await tenantsPromise,
        });
        setModel(model);
        setCleanModel(cloneDeep(model));
        const legacyOverflowActions: MenuItem[] = [
            OverflowMenuItems.deleteItemDefault("feature toggle", () => deleteFeature(model)),
            OverflowMenuItems.dialogItem("Clone", <CloneToggleDialog project={project} featureToggle={model} onCloned={onCloned} busy={busy} errors={errors} doBusyTask={doBusyTask}/>),
        ];
        setOverflowMenu(OverflowMenuConverterVNext.convertAll(legacyOverflowActions));
    }, []);
    async function deleteFeature(toDelete: FeatureToggleResource): Promise<boolean> {
        if (!toDelete)
            return false;
        await repository.FeatureToggles.delete(project, toDelete.Id);
        navigate(links.featureTogglesPage.generateUrl({ spaceId: project.SpaceId, projectSlug: project.Slug }));
        return true;
    }
    async function handleSaveClick() {
        if (!model)
            return;
        await doBusyTask(async () => {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            const existingModel = model as FeatureToggleResource;
            const updateResponse = await repository.FeatureToggles.modify(project, {
                ...existingModel,
                Environments: existingModel.Environments.map((t) => ({
                    ...t,
                })),
            });
            setModel(updateResponse);
            setCleanModel(cloneDeep(updateResponse));
            if (featureToggleSlug !== updateResponse.Slug) {
                navigate(links.featureTogglePage.generateUrl({ spaceId: project.SpaceId, projectSlug: project.Slug, featureToggleSlug: updateResponse.Slug }));
            }
        });
    }
    async function onCloned(clonedFeatureToggle: FeatureToggleResource) {
        navigate(links.featureTogglePage.generateUrl({ spaceId: project.SpaceId, projectSlug: project.Slug, featureToggleSlug: clonedFeatureToggle.Slug }));
    }
    return (<LegacyForm onSaveClick={handleSaveClick} model={model} cleanModel={cleanModel}>
            {({ FormContent, createSaveAction }) => (<PageContent header={{
                title: model?.Name ?? "",
                overflowActions: overflowMenu.menuItems,
                breadcrumbs: [{ label: "Feature Toggles", pageUrl: links.featureTogglesPage.generateUrl({ spaceId: project.SpaceId, projectSlug: project.Slug }) }],
                primaryAction: createSaveAction({}),
            }} busy={busy} errors={errors}>
                    <FormContent>
                        {overflowMenu.dialogs}
                        {lookupData && model && <EditFeatureToggle environments={lookupData.environments} tenants={lookupData.tenants} model={model} setModel={setModel}/>}
                    </FormContent>
                </PageContent>)}
        </LegacyForm>);
}
interface EditFeatureToggleProps {
    environments: EnvironmentResource[];
    tenants: TenantResource[];
    model: FeatureToggleResource;
    setModel: React.Dispatch<React.SetStateAction<FeatureToggleResource | undefined>>;
}
function EditFeatureToggle({ environments, tenants, model, setModel }: EditFeatureToggleProps) {
    const [editState, setEditState] = useState<EditState | null>(null);
    const [originalSlug] = useState(model.Slug);
    function nameSummary() {
        if (!model.Name)
            return Summary.placeholder("Please enter a name for the feature");
        return Summary.summary(<NameSummaryWithSlug name={model.Name} slug={model.Slug}/>);
    }
    function noteSummary() {
        if (!model.Description)
            return Summary.placeholder("Enter markdown notes");
        return Summary.summary(<Markdown markup={model.Description}/>);
    }
    function defaultValueSummary() {
        return Summary.summary(model.DefaultIsEnabled ? "On" : "Off");
    }
    function environmentRow(toggle: FeatureToggleEnvironmentResource, idx: number) {
        const environment = environments.find((e) => e.Id === toggle.DeploymentEnvironmentId);
        if (!environment) {
            throw new Error("Could not find environment");
        }
        const rowTenants = tenants.filter((t) => toggle.TenantIds.indexOf(t.Id) !== -1 || toggle.ExcludedTenantIds.indexOf(t.Id) !== -1);
        const handleClick = (e: React.MouseEvent<HTMLSpanElement>) => e.stopPropagation();
        return (<div>
                <span onClick={handleClick}>
                    <Switch value={toggle.IsEnabled} onChange={(value) => setToggleEnvironment(value, idx)}/>
                </span>
                <ToggleScope featureToggleEnvironment={toggle} environmentName={environment.Name} tenants={rowTenants}/>
                {toggle.MinimumVersion && (<>
                        <code>&gt;= {toggle.MinimumVersion}</code>
                    </>)}
            </div>);
    }
    function setToggleEnvironment(value: boolean, idx: number) {
        const toggleEnvironments = [...model.Environments];
        toggleEnvironments[idx] = { ...toggleEnvironments[idx], IsEnabled: value };
        setModel({ ...model, Environments: toggleEnvironments });
    }
    async function addToggleEnvironment(environment: FeatureToggleEnvironmentResource) {
        setModel({ ...model, Environments: [...model.Environments, environment] });
        return true;
    }
    async function saveToggleEnvironment(environment: FeatureToggleEnvironmentResource) {
        const toggles = [...model.Environments];
        if (editState && editState.environmentEditIdx !== null) {
            toggles[editState.environmentEditIdx] = environment;
        }
        else {
            toggles.push(environment);
        }
        setModel({ ...model, Environments: toggles });
        return true;
    }
    async function removeToggleEnvironment(toggle: FeatureToggleEnvironmentResource) {
        setModel({
            ...model,
            Environments: model.Environments.filter((t) => t !== toggle),
        });
    }
    return (<div>
            {editState && (<DialogOpener open={true} onClose={() => setEditState(null)} wideDialog={true}>
                    <EditToggleDialog title="Edit Environment" onSave={saveToggleEnvironment} environments={environments} tenants={tenants} featureToggleEnvironment={editState.environmentEdit}/>
                </DialogOpener>)}

            <ExpandableFormSection errorKey="Name" title="Name" summary={nameSummary()}>
                <Text value={model.Name} label={"Feature Name"} validate={required("Please provide a name for the feature")} onChange={(value) => {
            setModel({ ...model, Name: value });
        }}/>
                <SlugEditor label="Slug" value={model.Slug} originalSlug={originalSlug} readOnly={false} onChange={(value) => {
            setModel({ ...model, Slug: value });
        }} additionalWarningMessage={"Modifying a slug may impact feature toggle evaluations for software referencing the slug. Any references to the old slug will have to be manually updated, and careful consideration should be given to managing the changeover from the old slug to the new slug."} validate={required("Please enter a feature toggle slug")}/>
            </ExpandableFormSection>
            <ExpandableFormSection errorKey="Description" title="Notes" summary={noteSummary()}>
                <MarkdownEditor label="Notes" value={model.Description} onChange={(value) => {
            setModel({ ...model, Description: value });
        }}/>
            </ExpandableFormSection>
            <ExpandableFormSection title="Default Value" errorKey="DefaultValue" summary={defaultValueSummary()} help={"The default value will be returned for any environments not configured below"}>
                <Switch value={model.DefaultIsEnabled} onChange={(value) => {
            setModel({ ...model, DefaultIsEnabled: value });
        }}/>
            </ExpandableFormSection>
            <FormSection title="Environments" includeBorder={true}>
                <OpenDialogButton label="Add Environment" type={ActionButtonType.Secondary} dialogWidth={"1000px"}>
                    <EditToggleDialog title="Add Environment" onSave={addToggleEnvironment} environments={environments} tenants={tenants}/>
                </OpenDialogButton>
                <ToggleEnvironmentList data={model.Environments} onRow={(toggle, idx) => environmentRow(toggle, idx)} onRowTouch={(t) => setEditState({ environmentEdit: t, environmentEditIdx: model.Environments.indexOf(t) })} onRemoveRow={removeToggleEnvironment}/>
            </FormSection>
        </div>);
}
interface CloneToggleDialogProps {
    project: ProjectResource;
    featureToggle: FeatureToggleResource;
    onCloned(featureToggleResource: FeatureToggleResource): void;
    doBusyTask: DoBusyTask;
    busy: Promise<void> | undefined;
    errors: Errors | undefined;
}
function CloneToggleDialog(props: CloneToggleDialogProps) {
    const { project, featureToggle, doBusyTask, busy, errors } = props;
    const [name, setName] = useState<string>(props.featureToggle.Name);
    async function onClone() {
        await doBusyTask(async () => {
            const clonedFeatureToggle = await repository.FeatureToggles.clone(project, featureToggle.Id, name);
            props.onCloned(clonedFeatureToggle);
        });
        return true;
    }
    return (<SaveDialogLayout title="Clone Feature Toggle" cancelButtonLabel="Cancel" saveButtonLabel="Clone" errors={errors} busy={busy} onSaveClick={onClone}>
            <React.Fragment>
                <Text value={name} label="Name" onChange={(value) => setName(value)} autoFocus/>
            </React.Fragment>
        </SaveDialogLayout>);
}
